import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDateTime, fmtDuration, clockOf, errMsg } from "@/lib/api";
import { StatCard, SectionHead, StatusPill, ConfBadge, Empty, Skeletons } from "@/components/Primitives";
import { AudioWave } from "@/components/AudioWave";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarCheck, Clock, ListChecks, Mic2, Gauge, Radio, ArrowUpRight, Users, FileAudio, Sparkles, Plus,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const quickStart = async () => {
    try {
      const { data } = await api.post("/meetings", {
        title: `Rapat Cepat — ${new Date().toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`,
        agenda: "Rapat ad-hoc dimulai dari dashboard",
        location: "Online", division: "", organization: "", tags: ["quick-start"],
        scheduled_at: new Date().toISOString(), duration_minutes: 30, language: "id",
        participant_ids: [], status: "scheduled",
      });
      toast.success("Rapat dibuat, mengalihkan ke live session…");
      nav(`/live/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="grid gap-6"><Skeletons rows={2} className="h-28" /><Skeletons rows={2} className="h-64" /></div>;
  if (!s) return <Empty icon={Gauge} title="Data tidak tersedia" sub="Gagal memuat statistik dashboard." />;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs">Ringkasan Operasional</p>
          <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-2 text-sm text-slate-500">Intelijen rapat organisasi Anda dalam satu tampilan.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="h-11 rounded-xl border-slate-200" onClick={() => nav("/meetings/new")} data-testid="dashboard-create-meeting-btn">
            <Plus size={16} className="mr-2" /> Buat Rapat
          </Button>
          <Button className="h-11 rounded-xl grad-brand text-white px-5" onClick={quickStart} data-testid="quick-start-meeting-btn">
            <Radio size={16} className="mr-2" /> Quick Start Meeting
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <StatCard testId="stat-total-meetings" label="Total Meeting" value={s.total_meetings} sub={`${s.total_users} pengguna terdaftar`} icon={FileAudio} delay={0} />
        <StatCard testId="stat-todays-meetings" label="Today's Meeting" value={s.todays_meetings} sub="Rapat terjadwal hari ini" icon={CalendarCheck} accent="cyan" delay={60} />
        <StatCard testId="stat-total-duration" label="Total Duration" value={s.total_duration_label} sub="Akumulasi durasi terekam" icon={Clock} accent="slate" delay={120} />
        <StatCard testId="stat-pending-actions" label="Pending Action Item" value={s.pending_action_items} sub="Menunggu penyelesaian" icon={ListChecks} accent="brand" delay={180} />
        <StatCard testId="stat-active-speaker" label="Most Active Speaker" value={s.most_active_speaker?.name || "—"}
          sub={s.most_active_speaker ? `${s.most_active_speaker.segments} segmen · ${fmtDuration(s.most_active_speaker.duration)}` : "Belum ada transkrip"}
          icon={Mic2} accent="cyan" delay={240} />
        <StatCard testId="stat-accuracy" label="Average Accuracy" value={`${(s.average_accuracy * 100).toFixed(1)}%`} sub={`${s.enrolled_voices} voiceprint terdaftar`} icon={Gauge} accent="green" delay={300} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="clay p-6 xl:col-span-2" data-testid="dashboard-trend-chart">
          <SectionHead title="Aktivitas 7 Hari" sub="Jumlah rapat dan total menit terekam" />
          <div className="h-[260px] w-full min-w-0 -ml-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.trend}>
                <defs>
                  <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1D4ED8" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#1D4ED8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gB" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Area type="monotone" dataKey="meetings" name="Rapat" stroke="#1D4ED8" strokeWidth={2.4} fill="url(#gA)" />
                <Area type="monotone" dataKey="minutes" name="Menit" stroke="#06B6D4" strokeWidth={2} fill="url(#gB)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="clay p-6 flex flex-col" data-testid="dashboard-speaker-chart">
          <SectionHead title="Speaker Teraktif" sub="Berdasarkan jumlah segmen" />
          {s.speaker_stats?.length ? (
            <div className="h-[260px] w-full min-w-0 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.speaker_stats} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={11} width={90} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="segments" name="Segmen" radius={[0, 8, 8, 0]} barSize={16}>
                    {s.speaker_stats.map((_, i) => (
                      <Cell key={i} fill={["#1D4ED8", "#2563EB", "#06B6D4", "#60A5FA", "#0EA5E9", "#6366F1"][i % 6]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Empty icon={Users} title="Belum ada data speaker" sub="Unggah audio rapat atau jalankan sesi live untuk melihat statistik." />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="clay p-6 xl:col-span-2" data-testid="latest-meetings-card">
          <SectionHead title="Latest Meeting" sub="Rapat terbaru pada organisasi Anda"
            right={<Link to="/meetings"><Button variant="ghost" size="sm" className="text-brand-600" data-testid="view-all-meetings-btn">Lihat semua <ArrowUpRight size={14} className="ml-1" /></Button></Link>} />
          {s.latest_meetings?.length ? (
            <div className="divide-y divide-slate-100">
              {s.latest_meetings.map((m) => (
                <Link key={m.id} to={`/meetings/${m.id}`} className="group flex items-center gap-4 py-4 first:pt-0" data-testid={`latest-meeting-${m.id}`}>
                  <div className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-brand-50 text-brand-600 group-hover:grad-brand group-hover:text-white transition-colors">
                    <FileAudio size={17} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800 group-hover:text-brand-700 transition-colors">{m.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {fmtDateTime(m.scheduled_at)} · {m.location || "—"} · {m.participant_count} peserta
                    </p>
                  </div>
                  <div className="hidden sm:block"><StatusPill status={m.status} /></div>
                </Link>
              ))}
            </div>
          ) : (
            <Empty icon={FileAudio} title="Belum ada rapat" sub="Mulai dengan membuat rapat pertama Anda."
              action={<Button className="rounded-xl grad-brand text-white" onClick={() => nav("/meetings/new")}>Buat Rapat</Button>} />
          )}
        </div>

        <div className="clay overflow-hidden" data-testid="quick-live-card">
          <div className="relative p-6 grad-brand">
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-white/70">Realtime Engine</p>
            <h3 className="mt-3 font-display text-xl font-semibold text-white">Live Transcription siap</h3>
            <p className="mt-2 text-sm text-white/80">Whisper Large V3 · Pyannote diarization · Voice biometrics.</p>
            <div className="mt-5 opacity-90"><AudioWave bars={34} height={44} /></div>
          </div>
          <div className="p-6">
            <p className="label-xs">Recent Transcript</p>
            <div className="mt-4 space-y-3.5 max-h-[220px] overflow-y-auto pr-1">
              {s.recent_transcripts?.length ? s.recent_transcripts.map((t) => (
                <div key={t.id} className="border-l-2 border-brand-100 pl-3" data-testid={`recent-transcript-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{clockOf(t.created_at)}</span>
                    <span className="text-xs font-semibold text-slate-700">{t.speaker_name}</span>
                    <ConfBadge value={t.confidence} />
                  </div>
                  <p className="mt-1 text-sm text-slate-600 leading-snug line-clamp-2">{t.text}</p>
                </div>
              )) : (
                <p className="text-sm text-slate-400">Belum ada transkrip. Jalankan sesi live atau unggah audio.</p>
              )}
            </div>
            <Button variant="outline" className="mt-5 w-full rounded-xl border-slate-200" onClick={() => nav("/live")} data-testid="goto-live-btn">
              <Sparkles size={15} className="mr-2 text-brand-600" /> Buka Realtime Transcript
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
